import { useTranslation } from 'react-i18next'
import { usePantry } from './usePantry'
import { useUpdatePantryState } from './useUpdatePantryState'
import { useRemoveCustomItem } from './useRemoveCustomItem'
import { usePantryRealtime } from './usePantryRealtime'
import { useUserRole } from '@/shared/hooks'
import { PantryItemRow } from './PantryItemRow'
import { HouseholdEssentialsSection } from './HouseholdEssentialsSection'
import type { PantryState } from './types'

const SECTION_ORDER: PantryState[] = ['need', 'running_low', 'own']

export function PantryView() {
  const { t } = useTranslation('pantry')
  const { data: ingredients, isLoading, isError } = usePantry()
  const updateMutation = useUpdatePantryState()
  const removeMutation = useRemoveCustomItem()
  const role = useUserRole()
  usePantryRealtime()

  if (isLoading) {
    return (
      <div className="px-4 pt-4 pb-24 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-14 rounded-xl bg-surface animate-pulse" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="px-4 pt-4 pb-24">
        <p className="text-danger text-sm" role="alert">
          {t('loadError')}
        </p>
      </div>
    )
  }

  const essentials = ingredients?.filter((i) => i.alwaysInclude) ?? []
  const regular = ingredients?.filter((i) => !i.alwaysInclude) ?? []
  const canManage = role === 'Planner'

  return (
    <div className="px-4 pt-4 pb-24 mx-auto w-full max-w-[600px]">
      <h1 className="text-xl font-bold text-ink mb-4">{t('title')}</h1>

      <HouseholdEssentialsSection
        items={essentials}
        onRemove={(id) => removeMutation.mutate(id)}
        isPending={removeMutation.isPending}
        canManage={canManage}
      />

      {SECTION_ORDER.map((state) => {
        const items = regular.filter((i) => i.pantryState === state)
        if (items.length === 0) return null
        return (
          <section key={state} aria-label={t(`section.${state}`)} className="mb-6">
            <h2 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
              {t(`section.${state}`)}
            </h2>
            <ul className="divide-y divide-line">
              {items.map((ingredient) => (
                <li key={ingredient.id}>
                  <PantryItemRow
                    ingredient={ingredient}
                    onStateChange={(id, newState) =>
                      updateMutation.mutate({ id, pantryState: newState })
                    }
                    isPending={
                      updateMutation.isPending &&
                      updateMutation.variables?.id === ingredient.id
                    }
                  />
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
